/**
 * Sanitizes a filename to be safe for use as an S3 object key.
 *
 * Per AWS documentation (https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-key-naming-guidance.html),
 * the following characters are safe without special handling:
 *   - Alphanumeric: a-z, A-Z, 0-9
 *   - Special: hyphen (-), underscore (_), period (.)
 *
 * This function:
 *   1. Splits name and extension to preserve the extension dot
 *   2. Replaces whitespace with underscores
 *   3. Removes all characters outside [a-zA-Z0-9._-]
 *   4. Collapses consecutive separators (e.g. "__" → "_")
 *   5. Strips leading/trailing separators
 *   6. Falls back to "file" if the sanitized name is empty
 */

const S3_SAFE_FILENAME_REGEX = /[^a-zA-Z0-9._-]/g;
const CONSECUTIVE_SEPARATORS_REGEX = /[_-]{2,}/g;
const LEADING_TRAILING_SEPARATORS_REGEX = /^[._-]+|[._-]+$/g;
const EXTENSION_SAFE_REGEX = /[^a-zA-Z0-9]/g;
const FALLBACK_FILENAME = 'file';

export function sanitizeFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  const hasExtension = lastDot > 0;

  const rawName = hasExtension ? fileName.slice(0, lastDot) : fileName;
  const rawExt = hasExtension ? fileName.slice(lastDot + 1) : '';

  const sanitizedName = rawName
    .replace(/\s+/g, '_')
    .replace(S3_SAFE_FILENAME_REGEX, '')
    .replace(CONSECUTIVE_SEPARATORS_REGEX, '_')
    .replace(LEADING_TRAILING_SEPARATORS_REGEX, '');

  const sanitizedExt = rawExt.replace(EXTENSION_SAFE_REGEX, '');

  const name = sanitizedName || FALLBACK_FILENAME;
  return sanitizedExt ? `${name}.${sanitizedExt}` : name;
}
